const CATALOG_PRODUCT_SECTION_KEYS = [
  'tests',
  'analytics',
  'development',
  'professionalDevelopment',
  'expertSupport',
];

const DEVELOPMENT_EXCLUDED_ORDER_KEYS = new Set([
  'hr_ei_program',
  'emotional_leadership_masterclass',
]);

function filterDevelopmentSectionProducts(products = []) {
  return products.filter((product) => {
    const orderKey = String(product.orderProductKey ?? '');

    if (DEVELOPMENT_EXCLUDED_ORDER_KEYS.has(orderKey)) {
      return false;
    }

    const title = String(product.title ?? '');

    return !title.includes('Программа для HR')
        && !title.includes('Мастер-класс «Эмоциональное лидерство»');
  });
}

export function buildCatalogProductsModel(catalogConfig, productsBySection = {}, quotaOptionsBySection = {}) {
  const merged = {
    ...catalogConfig,
    quotaOptionsBySection,
  };

  CATALOG_PRODUCT_SECTION_KEYS.forEach((sectionKey) => {
    const apiProducts = productsBySection[sectionKey];
    const fallbackProducts = catalogConfig[sectionKey] ?? [];
    const sectionProducts = apiProducts?.length ? apiProducts : fallbackProducts;

    merged[sectionKey] = sectionKey === 'development'
        ? filterDevelopmentSectionProducts(sectionProducts)
        : sectionProducts;
  });

  return merged;
}

export function resolveCatalogSectionOrderKey(catalogProducts, sectionKey) {
  const section = (catalogProducts.sections ?? []).find((item) => item.key === sectionKey);

  return section?.orderKey || null;
}

export const DEFAULT_CATALOG_EMPLOYEE_COUNT = 1;
export const CATALOG_ORDER_MIN_BULK = 10;
export const CATALOG_ORDER_MIN_SINGLE = 1;

const normalizeCatalogTitle = (value) => String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export function parseCatalogFixedPrice(priceText) {
  if (priceText == null || String(priceText).trim() === '') {
    return null;
  }

  const digits = String(priceText).replace(/[^\d]/g, '');

  return digits ? Number(digits) : null;
}

export function buildCatalogPriceRules(catalogProducts) {
  const rules = {};

  (catalogProducts.sections ?? []).forEach((section) => {
    (catalogProducts[section.key] ?? []).forEach((product) => {
      if (!product.prices || !product.orderProductKey) {
        return;
      }

      rules[String(product.orderProductKey)] = product.prices;
    });
  });

  return rules;
}

export function buildCatalogFixedPriceRules(catalogProducts) {
  const rules = {};

  (catalogProducts.sections ?? []).forEach((section) => {
    (catalogProducts[section.key] ?? []).forEach((product) => {
      if (!product.orderProductKey) {
        return;
      }

      const fixedPrice = parseCatalogFixedPrice(product.priceText);

      if (fixedPrice != null) {
        rules[String(product.orderProductKey)] = fixedPrice;
      }
    });
  });

  return rules;
}

export const CATALOG_CONTEXT_SECTIONS = [
  'analytics',
  'development',
  'professionalDevelopment',
  'expertSupport',
];

export const CATALOG_ORDER_SECTIONS = [
  'tests',
  ...CATALOG_CONTEXT_SECTIONS,
];

const ORDER_TABLE_SECTIONS = {
  test: ['tests'],
  courses: ['development'],
  professionalDevelopment: ['professionalDevelopment'],
  expertSupport: ['expertSupport'],
  analytics: ['analytics'],
};

export function getCatalogSectionProducts(sectionKey, catalogProducts) {
  return catalogProducts[sectionKey] ?? [];
}

export function resolveCatalogProductOrderKey(catalogProduct, sectionKey, index) {
  if (catalogProduct.orderProductKey) {
    return String(catalogProduct.orderProductKey);
  }

  return `catalog_${sectionKey}_${index}`;
}

export function createCatalogOrderRow(catalogProduct, productId) {
  const fixedPrice = parseCatalogFixedPrice(catalogProduct.priceText);

  return {
    product: catalogProduct.title,
    product_id: productId,
    free: 0,
    quota: 0,
    annual_price: fixedPrice ?? '',
    original_price: fixedPrice ?? '',
    total_price: 0,
  };
}

export function resolveCompanyFreeForCatalogProduct(sectionKey, productId, companyQuotas) {
  if (!companyQuotas) {
    return 0;
  }

  if (sectionKey === 'tests') {
    return companyQuotas.test_passings?.[productId]?.free ?? 0;
  }

  if (sectionKey === 'development') {
    const freeEntry = companyQuotas.course_passings?.free?.find(
        (course) => String(course.course_id) === String(productId),
    );

    return freeEntry?.count ?? 0;
  }

  if (sectionKey === 'analytics' && productId === 'reference_profile') {
    return companyQuotas.reference_profiles?.count
        ?? companyQuotas.reference_profiles?.free_count
        ?? 0;
  }

  return 0;
}

export function buildCatalogOrderTableRows(sectionKey, catalogProducts, companyQuotas = null) {
  return getCatalogSectionProducts(sectionKey, catalogProducts).map((product, index) => {
    const productId = resolveCatalogProductOrderKey(product, sectionKey, index);
    const row = createCatalogOrderRow(product, productId);

    if (companyQuotas) {
      row.free = resolveCompanyFreeForCatalogProduct(sectionKey, productId, companyQuotas);
    }

    return row;
  });
}

export function buildCatalogOrderTablesState(
    catalogProducts,
    companyQuotas = null,
    orderTableAliases = {},
) {
  const tables = {};
  const entitiesByTable = {};

  CATALOG_ORDER_SECTIONS.forEach((sectionKey) => {
    const orderKey = resolveCatalogSectionOrderKey(catalogProducts, sectionKey);

    if (!orderKey) {
      return;
    }

    const tableKey = orderTableAliases[orderKey] ?? orderKey;
    const rows = buildCatalogOrderTableRows(sectionKey, catalogProducts, companyQuotas);

    tables[tableKey] = rows;
    entitiesByTable[tableKey] = { total: 0 };
    rows.forEach((row) => {
      entitiesByTable[tableKey][String(row.product_id)] = 0;
    });
  });

  return { tables, entitiesByTable };
}

export function getCatalogSectionKeysForTableKeys(tableKeys, catalogProducts, orderTableAliases = {}) {
  return CATALOG_ORDER_SECTIONS.filter((sectionKey) => {
    const orderKey = resolveCatalogSectionOrderKey(catalogProducts, sectionKey);
    const tableKey = orderTableAliases[orderKey] ?? orderKey;

    return tableKeys.includes(tableKey);
  });
}

export function getCatalogTableKeyFromSection(catalogProducts, sectionKey, orderTableAliases = {}) {
  const orderKey = resolveCatalogSectionOrderKey(catalogProducts, sectionKey);

  if (!orderKey) {
    return null;
  }

  return orderTableAliases[orderKey] ?? orderKey;
}

export function resolveCatalogTierQuantity(
    catalogPricingQuotas,
    tableKey,
    productId,
    orderCount,
) {
  const normalizedProductId = String(productId);
  const employeeTier = catalogPricingQuotas?.[tableKey]?.[normalizedProductId]
    ?? catalogPricingQuotas?.[tableKey]?.[productId];
  const normalizedOrderCount = Number(orderCount) || 0;

  if (normalizedOrderCount > 0) {
    return normalizedOrderCount;
  }

  if (employeeTier == null || employeeTier === '') {
    return 0;
  }

  const normalizedEmployeeTier = Number(employeeTier);

  return Number.isNaN(normalizedEmployeeTier) ? 0 : normalizedEmployeeTier;
}

function getCatalogItemsForTable(tableKey, catalogProducts) {
  const sectionKeys = ORDER_TABLE_SECTIONS[tableKey] ?? [];

  return sectionKeys.flatMap((sectionKey) => (
    (catalogProducts[sectionKey] ?? []).map((product) => ({ ...product, sectionKey }))
  ));
}

export function findCatalogProductForOrderRow(tableKey, row, catalogProducts) {
  if (!row) {
    return null;
  }

  const catalogItems = getCatalogItemsForTable(tableKey, catalogProducts);

  const byProductKey = catalogItems.find((product) => (
    product.orderProductKey && String(product.orderProductKey) === String(row.product_id)
  ));

  if (byProductKey) {
    return byProductKey;
  }

  const rowTitle = normalizeCatalogTitle(row.product);

  return catalogItems.find((product) => (
    normalizeCatalogTitle(product.title) === rowTitle
    || rowTitle.includes(normalizeCatalogTitle(product.title).slice(0, 40))
    || normalizeCatalogTitle(product.title).includes(rowTitle.slice(0, 40))
  )) ?? null;
}

export function resolveOrderProductMinCount(catalogProduct, options = {}) {
  if (!catalogProduct) {
    return 0;
  }

  if (catalogProduct.priceText && !catalogProduct.prices) {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  if (catalogProduct.skipEmployeeQuotaMin) {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  if (!catalogProduct.prices) {
    return 0;
  }

  const {
    catalogProducts,
    sectionKey,
    employeeTierValue,
  } = options;

  if (
    employeeTierValue != null
    && catalogProducts
    && sectionKey
  ) {
    return resolveCatalogQuotaMin(catalogProducts, sectionKey, employeeTierValue);
  }

  if (catalogProduct.sectionKey === 'professionalDevelopment') {
    return CATALOG_ORDER_MIN_SINGLE;
  }

  return CATALOG_ORDER_MIN_BULK;
}

export function resolveOrderRowMinCount(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  if (!catalogProduct) {
    return 0;
  }

  return resolveOrderProductMinCount(catalogProduct);
}

export function findCatalogPricesForOrderRow(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  return catalogProduct?.prices ?? null;
}

export function findCatalogFixedPriceForOrderRow(tableKey, row, catalogProducts) {
  const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

  return parseCatalogFixedPrice(catalogProduct?.priceText);
}

export function resolveOrderProductId(catalogProducts, catalogProduct, tableKey, rows) {
  if (!catalogProduct) {
    return null;
  }

  const orderProductKey = catalogProduct.orderProductKey
      ? String(catalogProduct.orderProductKey)
      : null;

  if (orderProductKey) {
    const rowByKey = (rows ?? []).find(
        (item) => String(item.product_id) === orderProductKey,
    );

    if (rowByKey) {
      return rowByKey.product_id;
    }
  }

  const catalogTitle = normalizeCatalogTitle(catalogProduct.title);

  const rowByTitle = (rows ?? []).find((item) => {
    const rowTitle = normalizeCatalogTitle(item.product);

    return rowTitle === catalogTitle
        || rowTitle.includes(catalogTitle.slice(0, 40))
        || catalogTitle.includes(rowTitle.slice(0, 40));
  });

  if (rowByTitle) {
    return rowByTitle.product_id;
  }

  return orderProductKey;
}

export function ensureCatalogOrderProductRow(
    tableData,
    orderFormEntities,
    tableKey,
    catalogProduct,
    sectionKey,
    catalogProducts,
    index = 0,
) {
  const rows = tableData[tableKey] ?? [];
  const fallbackProductId = resolveCatalogProductOrderKey(catalogProduct, sectionKey, index);
  const resolvedProductId = resolveOrderProductId(
      catalogProducts,
      catalogProduct,
      tableKey,
      rows,
  ) ?? fallbackProductId;

  let row = rows.find((item) => String(item.product_id) === String(resolvedProductId));

  if (!row) {
    row = createCatalogOrderRow(catalogProduct, resolvedProductId);
    tableData[tableKey].push(row);
  } else if (catalogProduct.title) {
    row.product = catalogProduct.title;
  }

  if (orderFormEntities[tableKey][resolvedProductId] == null) {
    orderFormEntities[tableKey][resolvedProductId] = 0;
  }

  return {
    productId: resolvedProductId,
    row,
  };
}

export function applyCatalogProductNamesToOrderRows(tableData, catalogProducts, tableKeys) {
  (tableKeys ?? []).forEach((tableKey) => {
    (tableData[tableKey] ?? []).forEach((row) => {
      if (!row) {
        return;
      }

      const catalogProduct = findCatalogProductForOrderRow(tableKey, row, catalogProducts);

      if (catalogProduct?.title) {
        row.product = catalogProduct.title;
      }
    });
  });
}

export function getTierPrice(prices, quantity) {
  if (!prices || quantity == null || quantity === '') {
    return null;
  }

  const normalizedQuantity = Number(quantity);

  if (Number.isNaN(normalizedQuantity)) {
    return null;
  }

  const tiers = Object.keys(prices)
      .map(Number)
      .filter((key) => !Number.isNaN(key))
      .sort((a, b) => a - b);

  if (!tiers.length) {
    return null;
  }

  const matchedTier = tiers.find((tier) => normalizedQuantity <= tier);
  const tierKey = matchedTier ?? tiers[tiers.length - 1];

  return prices[String(tierKey)] ?? prices[tierKey] ?? null;
}

export function buildQuotaOptionsFromPrices(prices, options = {}) {
  if (!prices || typeof prices !== 'object') {
    return [];
  }

  const minFirst = options.minFirst ?? 10;
  const tiers = Object.keys(prices)
      .map(Number)
      .filter((key) => !Number.isNaN(key))
      .sort((a, b) => a - b);

  if (!tiers.length) {
    return [];
  }

  let previousMax = 0;

  return tiers.map((max, index) => {
    const min = index === 0 ? minFirst : previousMax + 1;
    const isLastTier = index === tiers.length - 1;
    const label = isLastTier && max >= 10000
        ? `${min.toLocaleString('ru-RU')}+`
        : `${min}-${max}`;

    previousMax = max;

    return {
      label,
      value: max,
      min,
    };
  });
}

export function parseMinFromQuotaLabel(label) {
  const rangeMatch = String(label ?? '').match(/^([\d\s]+)\s*-/);

  if (rangeMatch) {
    return Number(rangeMatch[1].replace(/\s/g, ''));
  }

  const plusMatch = String(label ?? '').match(/^([\d\s]+)\s*\+/);

  if (plusMatch) {
    return Number(plusMatch[1].replace(/\s/g, ''));
  }

  return null;
}

export function enrichQuotaOptionsWithMin(options, minFirst = 10) {
  let previousMax = 0;

  return (options ?? []).map((option, index) => {
    if (option?.min != null) {
      previousMax = Number(option.value);
      return option;
    }

    const parsedMin = parseMinFromQuotaLabel(option?.label);
    const min = parsedMin ?? (index === 0 ? minFirst : previousMax + 1);

    previousMax = Number(option.value);

    return {
      ...option,
      min,
    };
  });
}

export function resolveCatalogQuotaMin(catalogProducts, sectionKey, tierValue) {
  const options = getCatalogSectionQuotaOptions(catalogProducts, sectionKey);
  const option = options.find((item) => Number(item.value) === Number(tierValue));

  let min = null;

  if (option?.label) {
    const parsedMin = parseMinFromQuotaLabel(option.label);

    if (parsedMin != null) {
      min = parsedMin;
    }
  }

  if (min == null && option?.min != null && Number(option.min) !== Number(option.value)) {
    min = Number(option.min);
  }

  if (min == null) {
    const section = (catalogProducts.sections ?? []).find((item) => item.key === sectionKey);
    const fallbackOptions = enrichQuotaOptionsWithMin(
        section?.quotaOptionsKey ? catalogProducts[section.quotaOptionsKey] : [],
        sectionKey === 'professionalDevelopment' ? 1 : 10,
    );
    const fallbackOption = fallbackOptions.find((item) => Number(item.value) === Number(tierValue));

    if (fallbackOption?.label) {
      const parsedFallbackMin = parseMinFromQuotaLabel(fallbackOption.label);

      if (parsedFallbackMin != null) {
        min = parsedFallbackMin;
      }
    }

    if (min == null && fallbackOption?.min != null) {
      min = Number(fallbackOption.min);
    }
  }

  if (min == null) {
    return sectionKey === 'professionalDevelopment'
        ? CATALOG_ORDER_MIN_SINGLE
        : CATALOG_ORDER_MIN_BULK;
  }

  if (sectionKey !== 'professionalDevelopment' && min < CATALOG_ORDER_MIN_BULK) {
    return CATALOG_ORDER_MIN_BULK;
  }

  return min;
}

export function buildSectionQuotaOptionsMap(productsBySection, catalogConfig) {
  const quotaOptionsBySection = {};

  (catalogConfig.sections ?? []).forEach((section) => {
    if (!section.showQuotaSelect) {
      return;
    }

    const sectionProducts = productsBySection[section.key] ?? [];
    const productWithPrices = sectionProducts.find(
        (product) => product.prices && Object.keys(product.prices).length,
    );

    if (productWithPrices?.priceTierOptions?.length) {
      quotaOptionsBySection[section.key] = productWithPrices.priceTierOptions;
      return;
    }

    if (productWithPrices) {
      const minFirst = section.key === 'professionalDevelopment'
          ? 1
          : 10;
      const options = buildQuotaOptionsFromPrices(
          productWithPrices.prices,
          { minFirst },
      );

      if (options.length) {
        quotaOptionsBySection[section.key] = options;
        return;
      }
    }

    if (section.quotaOptionsKey) {
      const minFirst = section.key === 'professionalDevelopment'
          ? 1
          : 10;

      quotaOptionsBySection[section.key] = enrichQuotaOptionsWithMin(
          catalogConfig[section.quotaOptionsKey] ?? [],
          minFirst,
      );
    }
  });

  return quotaOptionsBySection;
}

export function getCatalogSectionQuotaOptions(catalogProducts, sectionKey) {
  const section = (catalogProducts.sections ?? []).find((item) => item.key === sectionKey);

  if (!section?.showQuotaSelect) {
    return [];
  }

  const fromStore = catalogProducts.quotaOptionsBySection?.[sectionKey];

  if (fromStore?.length) {
    return fromStore;
  }

  return enrichQuotaOptionsWithMin(
      section.quotaOptionsKey ? catalogProducts[section.quotaOptionsKey] : [],
      sectionKey === 'professionalDevelopment' ? 1 : 10,
  );
}

export function formatCatalogPrice(value, options = {}) {
  if (value == null || value === '') {
    return '';
  }

  const formatted = `${Number(value).toLocaleString('ru-RU')} ₽`;

  return options.prefix ? `${options.prefix} ${formatted}` : formatted;
}

export function resolveCatalogSubscriptionProductId(catalogSubscription) {
  return catalogSubscription.title;
}

export function resolveCatalogSubscriptionInOrderForm(rows, catalogSubscription) {
  const productId = resolveCatalogSubscriptionProductId(catalogSubscription);

  const rowById = (rows ?? []).find((item) => String(item.product_id) === String(productId));

  if (rowById) {
    return { productId: rowById.product_id, row: rowById };
  }

  const rowByTitle = (rows ?? []).find((item) => (
    item.product === catalogSubscription.title
  ));

  if (rowByTitle) {
    return { productId: rowByTitle.product_id, row: rowByTitle };
  }

  return { productId, row: null };
}

export function createCatalogSubscriptionRow(catalogSubscription, productId) {
  const price = parseCatalogFixedPrice(catalogSubscription.price);

  return {
    product_id: productId,
    product: catalogSubscription.title,
    free: 0,
    has_access: false,
    quota: 0,
    annual_price: price ?? '',
    original_price: price ?? '',
    total_price: 0,
  };
}

export function resolveProductPriceDisplay(product, quota) {
  const fixedPrice = product.priceText != null && String(product.priceText).trim() !== ''
      ? String(product.priceText).trim()
      : null;

  if (fixedPrice) {
    return product.pricePrefix ? `${product.pricePrefix} ${fixedPrice}` : fixedPrice;
  }

  const price = getTierPrice(product.prices, quota);

  if (price == null) {
    return '';
  }

  return formatCatalogPrice(price, { prefix: product.pricePrefix });
}

export function resolveActiveCatalogSubscriptions(catalogSubscriptions = [], permittedSubscriptions = []) {
  const permittedByTitle = new Map(
      (permittedSubscriptions ?? [])
          .map((subscription) => [subscription.title, subscription])
          .filter(([title]) => title),
  );

  return (catalogSubscriptions ?? [])
      .map((subscription) => {
        const permittedSubscription = permittedByTitle.get(subscription.title);

        if (!permittedSubscription) {
          return null;
        }

        const assigned = permittedSubscription.assigned ?? 0;
        const available = permittedSubscription.available ?? 0;

        return {
          ...subscription,
          used: assigned,
          total: available + assigned,
          expires: `Истекает ${permittedSubscription.expired_at}`,
        };
      })
      .filter(Boolean);
}
